import { useState } from 'react';
import { Sparkles, Check, RotateCcw, MessageSquare } from 'lucide-react';
import { aiApi } from '../../../services/api';
import toast from 'react-hot-toast';
import Modal from '../../../components/Modal';

interface AIGeneratedTemplate {
  name: string;
  type: string;
  content: string;
  media_url: string;
  delay_type: string;
  delay_seconds: number;
}

interface AITemplateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

type Step = 'input' | 'loading' | 'preview' | 'confirming' | 'done';

const PRESETS = [
  {
    id: 'welcome',
    icon: '👋',
    title: 'Boas-vindas',
    description: 'Mensagens de boas-vindas para novos clientes',
    prompt: 'Crie 3 modelos de mensagens de boas-vindas para novos clientes de uma loja. Inclua variáveis de personalização com {{nome}}.',
  },
  {
    id: 'promo',
    icon: '🎉',
    title: 'Promoção',
    description: 'Divulgação de promoções e ofertas especiais',
    prompt: 'Crie 3 modelos de mensagens promocionais para divulgar ofertas e descontos especiais. Use tom animado e urgência com {{nome}}.',
  },
  {
    id: 'winner',
    icon: '🏆',
    title: 'Ganhador de Prêmio',
    description: 'Notificação de prêmio ganho na roda',
    prompt: 'Crie 3 modelos de mensagens para notificar clientes que ganharam um prêmio na roda de prêmios. Inclua instruções de retirada com {{nome}}.',
  },
  {
    id: 'birthday',
    icon: '🎂',
    title: 'Aniversário',
    description: 'Parabéns e ofertas de aniversário',
    prompt: 'Crie 3 modelos de mensagens de feliz aniversário para clientes, com ofertas especiais de presente. Use {{nome}}.',
  },
  {
    id: 'reengagement',
    icon: '💬',
    title: 'Reengajamento',
    description: 'Reativar clientes que não compram há tempo',
    prompt: 'Crie 3 modelos de mensagens de reengajamento para clientes que não visitam a loja há tempo. Tom amigável e incentivador com {{nome}}.',
  },
  {
    id: 'feedback',
    icon: '⭐',
    title: 'Feedback',
    description: 'Pedir avaliação ou opinião do cliente',
    prompt: 'Crie 2 modelos de mensagens pedindo feedback/avaliação após uma compra ou visita. Use tom educado e profissional com {{nome}}.',
  },
];

export default function AITemplateModal({ isOpen, onClose, onSuccess }: AITemplateModalProps) {
  const [step, setStep] = useState<Step>('input');
  const [prompt, setPrompt] = useState('');
  const [generatedTemplates, setGeneratedTemplates] = useState<AIGeneratedTemplate[]>([]);
  const [selectedTemplates, setSelectedTemplates] = useState<number[]>([]);
  const [error, setError] = useState('');

  const resetModal = () => {
    setStep('input');
    setPrompt('');
    setGeneratedTemplates([]);
    setSelectedTemplates([]);
    setError('');
  };

  const handleClose = () => {
    resetModal();
    onClose();
  };

  const handleGenerate = async () => {
    if (!prompt.trim()) {
      toast.error('Digite um comando para a IA');
      return;
    }

    setStep('loading');
    setError('');

    try {
      const response = await aiApi.generateMessageTemplates(prompt);
      if (response.preview && response.templates) {
        setGeneratedTemplates(response.templates);
        setSelectedTemplates(response.templates.map((_: any, i: number) => i));
        setStep('preview');
      }
    } catch (err: any) {
      setError(err.error || 'Erro ao gerar modelos. Tente novamente.');
      setStep('input');
      toast.error(err.error || 'Erro ao gerar modelos com IA');
    }
  };

  const handleConfirm = async () => {
    const templatesToSave = selectedTemplates.map(i => generatedTemplates[i]);
    if (templatesToSave.length === 0) {
      toast.error('Selecione pelo menos um modelo');
      return;
    }

    setStep('confirming');
    setError('');

    try {
      await aiApi.generateMessageTemplates(prompt, true, templatesToSave);
      setStep('done');
      toast.success('🎉 Modelos de mensagem criados com IA!');

      setTimeout(() => {
        onSuccess();
        handleClose();
      }, 1500);
    } catch (err: any) {
      setError(err.error || 'Erro ao salvar modelos. Tente novamente.');
      setStep('preview');
      toast.error(err.error || 'Erro ao salvar modelos');
    }
  };

  const toggleTemplateSelection = (index: number) => {
    setSelectedTemplates(prev =>
      prev.includes(index)
        ? prev.filter(i => i !== index)
        : [...prev, index]
    );
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="✨ Criar Modelos com IA" size="xl">
      <div className="space-y-4">
        {/* Step: Input */}
        {step === 'input' && (
          <>
            <div>
              <p className="text-sm font-medium text-gray-700 mb-3">
                Escolha um exemplo ou descreva o tipo de mensagem:
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {PRESETS.map((preset) => (
                  <button
                    key={preset.id}
                    onClick={() => setPrompt(preset.prompt)}
                    className={`group relative rounded-xl border-2 p-4 text-left transition-all duration-200 hover:shadow-md ${prompt === preset.prompt
                      ? 'border-purple-500 bg-purple-50 shadow-md'
                      : 'border-gray-200 bg-white hover:border-purple-300 hover:bg-purple-50/50'
                      }`}
                  >
                    <div className="flex items-start gap-3">
                      <span className="text-2xl flex-shrink-0">{preset.icon}</span>
                      <div className="min-w-0">
                        <h4 className="text-sm font-semibold text-gray-900 group-hover:text-purple-700 transition-colors">
                          {preset.title}
                        </h4>
                        <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">
                          {preset.description}
                        </p>
                      </div>
                    </div>
                    {prompt === preset.prompt && (
                      <div className="absolute top-2 right-2">
                        <Check className="h-4 w-4 text-purple-600" />
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </div>

            <div className="relative">
              <textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder="Ex: Crie mensagens de promoção para uma pizzaria com tom informal e descontos de 10% e 20%..."
                className="w-full h-24 px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-purple-500 focus:ring-2 focus:ring-purple-200 text-sm resize-none transition-all outline-none"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                    handleGenerate();
                  }
                }}
              />
              <div className="absolute bottom-3 right-3">
                <span className="text-xs text-gray-400 hidden sm:inline">⌘+Enter para enviar</span>
              </div>
            </div>

            {error && (
              <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-700">
                {error}
              </div>
            )}

            <div className="flex justify-end">
              <button
                onClick={handleGenerate}
                disabled={!prompt.trim()}
                className="inline-flex items-center justify-center gap-2 px-6 py-2.5 rounded-xl text-white font-semibold text-sm bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 shadow-lg shadow-purple-200 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 hover:shadow-xl hover:shadow-purple-300"
              >
                <Sparkles className="h-4 w-4" />
                Gerar Modelos
              </button>
            </div>
          </>
        )}

        {/* Step: Loading */}
        {step === 'loading' && (
          <div className="flex flex-col items-center justify-center py-12 space-y-4">
            <div className="relative">
              <div className="w-16 h-16 rounded-full bg-gradient-to-tr from-purple-500 to-indigo-500 flex items-center justify-center animate-pulse">
                <Sparkles className="h-8 w-8 text-white" />
              </div>
              <div className="absolute -inset-2 rounded-full border-2 border-purple-300 border-t-purple-600 animate-spin" />
            </div>
            <div className="text-center">
              <h3 className="text-lg font-semibold text-gray-900">A IA está criando seus modelos...</h3>
              <p className="text-sm text-gray-500 mt-1">Gerando mensagens personalizadas</p>
            </div>
          </div>
        )}

        {/* Step: Preview */}
        {step === 'preview' && generatedTemplates.length > 0 && (
          <>
            <div className="flex items-center gap-2 mb-1">
              <MessageSquare className="h-4 w-4 text-gray-500" />
              <h4 className="text-sm font-semibold text-gray-700">
                {generatedTemplates.length} Modelo(s) Gerado(s) — selecione os que deseja salvar:
              </h4>
            </div>

            <div className="space-y-3 max-h-96 overflow-y-auto pr-1">
              {generatedTemplates.map((tmpl, index) => (
                <div
                  key={index}
                  onClick={() => toggleTemplateSelection(index)}
                  className={`rounded-xl border-2 p-4 cursor-pointer transition-all duration-200 ${selectedTemplates.includes(index)
                    ? 'border-purple-500 bg-purple-50/50'
                    : 'border-gray-200 bg-white hover:border-gray-300'
                    }`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-6 h-6 rounded-md border-2 flex-shrink-0 flex items-center justify-center mt-0.5 ${selectedTemplates.includes(index)
                      ? 'border-purple-500 bg-purple-500'
                      : 'border-gray-300 bg-white'
                      }`}>
                      {selectedTemplates.includes(index) && (
                        <Check className="h-3.5 w-3.5 text-white" />
                      )}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h5 className="text-sm font-semibold text-gray-900">{tmpl.name}</h5>
                        <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
                          {tmpl.type === 'text' ? 'Texto' : tmpl.type}
                        </span>
                      </div>
                      <div className="bg-white rounded-lg border border-gray-200 p-3 mt-2">
                        <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
                          {tmpl.content}
                        </p>
                      </div>
                      <p className="text-xs text-gray-400 mt-2">
                        Delay: {tmpl.delay_type === 'auto' ? 'Automático' : tmpl.delay_type === 'random' ? 'Aleatório' : `Fixo (${tmpl.delay_seconds}s)`}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {error && (
              <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-700">
                {error}
              </div>
            )}

            <div className="flex items-center justify-between pt-2">
              <button
                onClick={() => { setStep('input'); setGeneratedTemplates([]); setSelectedTemplates([]); }}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 transition-colors"
              >
                <RotateCcw className="h-4 w-4" />
                Refazer
              </button>

              <button
                onClick={handleConfirm}
                disabled={selectedTemplates.length === 0}
                className="inline-flex items-center gap-2 px-6 py-2.5 rounded-xl text-white font-semibold text-sm bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 shadow-lg shadow-green-200 transition-all duration-200 hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Check className="h-4 w-4" />
                Salvar {selectedTemplates.length} Modelo(s)
              </button>
            </div>
          </>
        )}

        {/* Step: Confirming */}
        {step === 'confirming' && (
          <div className="flex flex-col items-center justify-center py-12 space-y-4">
            <div className="relative">
              <div className="w-16 h-16 rounded-full bg-gradient-to-tr from-green-500 to-emerald-500 flex items-center justify-center animate-pulse">
                <MessageSquare className="h-8 w-8 text-white" />
              </div>
              <div className="absolute -inset-2 rounded-full border-2 border-green-300 border-t-green-600 animate-spin" />
            </div>
            <div className="text-center">
              <h3 className="text-lg font-semibold text-gray-900">Salvando modelos...</h3>
              <p className="text-sm text-gray-500 mt-1">Criando seus modelos de mensagem</p>
            </div>
          </div>
        )}

        {/* Step: Done */}
        {step === 'done' && (
          <div className="flex flex-col items-center justify-center py-12 space-y-4">
            <div className="w-16 h-16 rounded-full bg-gradient-to-tr from-green-500 to-emerald-500 flex items-center justify-center">
              <Check className="h-8 w-8 text-white" />
            </div>
            <div className="text-center">
              <h3 className="text-lg font-semibold text-gray-900">🎉 Modelos criados!</h3>
              <p className="text-sm text-gray-500 mt-1">
                Seus modelos de mensagem foram salvos com sucesso
              </p>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}
